import { SVG } from '@web/shared/components/svg-icon/svg-icon.types';

export type MultiSidebarIcon =
  | { readonly primeIcon: string; readonly customIcon?: never }
  | { readonly customIcon: SVG; readonly primeIcon?: never };

/** L3 — navigable button */
export interface MultiSidebarLink {
  readonly kind: 'link';
  readonly id: string;
  readonly label: string;
  readonly icon: MultiSidebarIcon;
  readonly routerLink: string[];
  /** Shown in UI but ignored for L1/L2 route-owned expand & active rail. */
  readonly shortcut?: boolean;
}

/** L2 — expandable row that reveals L3 children */
export interface MultiSidebarSection {
  readonly kind: 'section';
  readonly id: string;
  readonly label: string;
  readonly icon: MultiSidebarIcon;
  readonly children: readonly MultiSidebarLink[];
}

/** L1 — collapsible text header with icon; children are L2 sections and/or L3 links */
export interface MultiSidebarGroup {
  readonly kind: 'group';
  readonly id: string;
  readonly label: string;
  readonly icon: MultiSidebarIcon;
  readonly children: readonly (MultiSidebarLink | MultiSidebarSection)[];
}

export type MultiSidebarL1 = MultiSidebarGroup;

export function collectGroupLinks(group: MultiSidebarGroup): MultiSidebarLink[] {
  return group.children.flatMap((child) => (child.kind === 'section' ? [...child.children] : [child]));
}

export function collectGroupOwnedLinks(group: MultiSidebarGroup): MultiSidebarLink[] {
  return collectGroupLinks(group).filter((link) => !link.shortcut);
}

export function sectionOwnsUrl(section: MultiSidebarSection, url: string): boolean {
  return section.children.some((link) => !link.shortcut && pathMatchesLink(url, link));
}

export function groupOwnsUrl(group: MultiSidebarGroup, url: string): boolean {
  return collectGroupOwnedLinks(group).some((link) => pathMatchesLink(url, link));
}

export function routerLinkToPath(routerLink: string[]): string {
  const parts = routerLink.filter((part) => part !== '/');
  return '/' + parts.join('/');
}

export function pathMatchesLink(url: string, link: MultiSidebarLink): boolean {
  const path = routerLinkToPath(link.routerLink);
  const normalized = url.split('?')[0].split('#')[0];
  return normalized === path || normalized.startsWith(path + '/');
}
